import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getProjects, createProject, deleteProject, getProjectInfo } from '../api';
import { ALL_FIELDS } from '../fields';
import { copyToClipboard } from '../clipboard';

const DEFAULT_ENABLED = Object.fromEntries(
  ALL_FIELDS.map((f) => [f.key, ['student_name', 'student_id'].includes(f.key)])
);

function CreateModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandFields, setExpandFields] = useState(true);
  const [enabledFields, setEnabledFields] = useState(DEFAULT_ENABLED);

  const toggleField = (key) =>
    setEnabledFields((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedCount = Object.values(enabledFields).filter(Boolean).length;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await createProject({
        name: name.trim(),
        description: description.trim() || null,
        delivery_date: deliveryDate || null,
        enabled_fields: enabledFields,
      });
      onCreate(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>New Project</h2>
          <p>Create a new ID card project for a batch or group.</p>
        </div>
        {error && <div className="alert alert-error mb-4">{error}</div>}
        <form onSubmit={handleCreate} className="stack stack-3">
          <div className="form-group">
            <label className="form-label">Project Name *</label>
            <input
              className="form-input"
              placeholder="e.g. Class 10 — 2024"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              rows={3}
              placeholder="Add any notes, instructions, or extra data for this project…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Delivery Date</label>
            <input
              className="form-input"
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              onClick={() => setExpandFields(!expandFields)}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '8px 12px',
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, cursor: 'pointer', marginBottom: 8,
              }}
            >
              <span className="form-label" style={{ margin: 0 }}>
                Card Fields &nbsp;<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({selectedCount} selected)</span>
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{expandFields ? '▲' : '▼'}</span>
            </button>
            {expandFields && (
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '6px 16px', padding: '10px 12px',
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 6,
              }}>
                {ALL_FIELDS.map((field) => (
                  <label
                    key={field.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      checked={enabledFields[field.key] || false}
                      onChange={() => toggleField(field.key)}
                      style={{ cursor: 'pointer' }}
                    />
                    {field.label}
                  </label>
                ))}
              </div>
            )}
            <p className="form-hint" style={{ marginTop: 6 }}>Select which fields appear on the generated cards.</p>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !name.trim()}>
              {loading ? <span className="spinner" /> : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({}); // projectId -> submission count (null = unavailable)
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyLink = async (p) => {
    const ok = await copyToClipboard(`${window.location.origin}/upload/${p.share_token}`);
    if (ok) {
      setCopiedId(p.id);
      setTimeout(() => setCopiedId((c) => (c === p.id ? null : c)), 1500);
    } else {
      alert('Could not copy automatically. Link:\n' + `${window.location.origin}/upload/${p.share_token}`);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    getProjects()
      .then((r) => {
        if (cancelled) return;
        const projs = r.data;
        setProjects(projs);
        setLoading(false);
        // Submission count comes from the public project-info endpoint
        // (no admin endpoint exposes it). It only answers for active projects,
        // so inactive/closed ones resolve to null and render as "—".
        Promise.all(
          projs.map((p) =>
            getProjectInfo(p.share_token)
              .then((res) => [p.id, res.data.student_count])
              .catch(() => [p.id, null])
          )
        ).then((entries) => {
          if (!cancelled) setCounts(Object.fromEntries(entries));
        });
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    try {
      await deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch {
      alert('Failed to delete project');
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? projects.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      )
    : projects;

  return (
    <Layout title="Projects">
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreate={(p) => {
            setProjects((prev) => [p, ...prev]);
            setCounts((prev) => ({ ...prev, [p.id]: 0 }));
          }}
        />
      )}

      <div className="page-header">
        <div className="page-header-text">
          <h1>Projects</h1>
          <p>Manage your ID card generation projects</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New Project
        </button>
      </div>

      {!loading && projects.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <input
            className="form-input"
            placeholder="Search by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
          <span style={{
            position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted)', fontSize: 15, pointerEvents: 'none',
          }}>⌕</span>
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 16, lineHeight: 1,
              }}
            >✕</button>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : projects.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span style={{ fontSize: 32 }}>⊞</span>
            <h3>No projects yet</h3>
            <p>Create a project, upload a template, and start generating ID cards.</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              Create First Project
            </button>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span style={{ fontSize: 32 }}>⌕</span>
            <h3>No results</h3>
            <p>No projects match <strong>"{search}"</strong>. Try a different search.</p>
          </div>
        </div>
      ) : (
        <div className="grid-2" style={{ gap: 16 }}>
          {visible.map((p) => (
            <div key={p.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{p.name}</div>
                  <div className="text-sm text-muted">
                    Created {new Date(p.created_at).toLocaleDateString()}
                    {p.delivery_date && (
                      <> &middot; Due {new Date(p.delivery_date).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <span className={`badge ${p.status === 'active' ? 'badge-green' : 'badge-gray'}`}>
                  {p.status}
                </span>
              </div>

              {p.description && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  {p.description}
                </p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={() => navigate(`/projects/${p.id}`)}>
                  Open
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleCopyLink(p)}
                  title="Copy client upload link"
                >
                  {copiedId === p.id ? '✓ Copied' : 'Copy Link'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>
                  Delete
                </button>
                <span
                  title="Submissions in this project"
                  style={{
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap',
                    color: '#0c4a6e',
                    background: '#e0f2fe',
                    border: '1px solid #7dd3fc',
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {counts[p.id] == null ? '—' : counts[p.id]} submission{counts[p.id] === 1 ? '' : 's'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
